/**
 * Performance logging utilities for Recap module.
 *
 * Three tiers of logging, toggled in dev only via the `RECAP_PERF`
 * localStorage key:
 *
 *   localStorage.setItem('RECAP_PERF', '1')         → benchmark logs only
 *     (perfMeasure — labelled timings like "hover→click", "open commit")
 *
 *   localStorage.setItem('RECAP_PERF', 'verbose')   → benchmarks + chatter
 *     (adds useRenderCount, useWhyDidYouRender, useMountTiming, perfTimestamp)
 *
 *   localStorage.setItem('RECAP_PERF', 'summary')   → grouped cycle report
 *     (suppresses per-step logs; collects every perfMeasure call into a
 *     debounced console.table — one structured row-per-step summary per
 *     render cycle. Use when you want to see "where did this refresh's
 *     total time go?" instead of scrolling through interleaved [PERF]
 *     lines. Row counts are auto-extracted from labels that include
 *     "(N POs)" / "(N rows)" patterns.)
 *
 *   localStorage.removeItem('RECAP_PERF')           → all off
 *
 * Default tier was added because the render/mount chatter floods the
 * console (hundreds of lines per partner-card open) and drowns out
 * the labelled benchmarks. Bump to `verbose` only when you actually
 * need the per-render-component trace; switch to `summary` when you
 * want the bird's-eye view of a single refresh's per-step cost.
 */

import { useRef, useEffect } from "react";

const IS_DEV = import.meta.env.DEV;

type PerfLevel = "off" | "benchmark" | "verbose" | "summary";

function level(): PerfLevel {
  if (!IS_DEV) return "off";
  try {
    const v = localStorage.getItem("RECAP_PERF");
    if (v === "verbose") return "verbose";
    if (v === "summary") return "summary";
    if (v === "1" || v === "true") return "benchmark";
    return "off";
  } catch {
    return "off";
  }
}

function isEnabled(): boolean {
  return level() !== "off";
}

function isVerbose(): boolean {
  return level() === "verbose";
}

function isSummary(): boolean {
  return level() === "summary";
}

// --- Summary collector ---
//
// In `summary` mode every perfMeasure call funnels into this map instead of
// printing inline. A debounced flush (~80 ms after the last step) emits one
// grouped console.table — one row per step, ordered by total ms. Row counts
// are pulled out of the label via the `(N POs)` / `(N rows)` regex so existing
// call sites don't need updating.

interface SummaryStep {
  label: string;
  ms: number;
  rows?: number;
  count: number;
}

const summaryCollector = new Map<string, SummaryStep>();
let flushTimer: ReturnType<typeof setTimeout> | null = null;
let cycleStart: number | null = null;
const FLUSH_DEBOUNCE_MS = 80;

const ROW_COUNT_PATTERN = /\((\d+)/;

function extractRows(label: string): number | undefined {
  // Matches the leading number in patterns like "(1247 POs, 0 cache hits)",
  // "(N→K)", "(N rows)". Returns the first integer in the first parenthesised
  // group, or undefined if none found.
  const match = ROW_COUNT_PATTERN.exec(label);
  return match ? Number(match[1]) : undefined;
}

function recordStep(label: string, ms: number): void {
  cycleStart ??= performance.now();
  const existing = summaryCollector.get(label);
  if (existing) {
    existing.ms += ms;
    existing.count += 1;
    // Last value wins — labels typically embed the most-recent row count.
    const rows = extractRows(label);
    if (rows != null) existing.rows = rows;
  } else {
    summaryCollector.set(label, {
      label,
      ms,
      rows: extractRows(label),
      count: 1,
    });
  }
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(flushSummary, FLUSH_DEBOUNCE_MS);
}

function flushSummary(): void {
  if (summaryCollector.size === 0) {
    flushTimer = null;
    cycleStart = null;
    return;
  }
  const steps = Array.from(summaryCollector.values()).sort(
    (a, b) => b.ms - a.ms,
  );
  const totalMs = steps.reduce((acc, s) => acc + s.ms, 0);
  const wallMs = cycleStart == null ? totalMs : performance.now() - cycleStart;
  const rowsForLabel = (label: string): string => {
    const step = steps.find((s) => s.label === label);
    return step?.rows == null ? "" : String(step.rows);
  };
  // `console.table` produces a clean grid in Chrome devtools; the
  // groupCollapsed wrapper gives the user a single foldable line so back-to-
  // back cycles don't bury the rest of the console. Plus a plain-text
  // fallback inside the group so saved console logs (which don't serialize
  // `console.table`) still carry the per-step breakdown.
  console.groupCollapsed(
    `%c[RECAP] cycle summary — ${totalMs.toFixed(1)} ms across ${steps.length} steps (wall ${wallMs.toFixed(1)} ms)`,
    "color: #4a90e2; font-weight: bold",
  );
  // Plain-text breakdown (captured by `Save as → Plain text` exports).
  // Format: `<ms>ms  ×<n>  rows=<rows>  <label>` — sorted desc by ms.
  for (const s of steps) {
    const ms = s.ms.toFixed(1).padStart(7);
    const n = String(s.count).padStart(3);
    const rows = s.rows == null ? "      " : String(s.rows).padStart(6);
    console.log(`[RECAP]   ${ms} ms  ×${n}  rows=${rows}  ${s.label}`);
  }
  console.table(
    steps.map((s) => ({
      step: s.label,
      ms: Number(s.ms.toFixed(1)),
      rows: rowsForLabel(s.label),
      n: s.count > 1 ? s.count : "",
    })),
  );
  console.groupEnd();
  summaryCollector.clear();
  flushTimer = null;
  cycleStart = null;
}

// --- Timing ---

/** Start a performance mark. Returns the start time. */
export function perfMark(): number {
  return performance.now();
}

/**
 * Log elapsed time since `start` if above threshold (default 2ms).
 *
 * In `summary` mode the per-step log is suppressed and the elapsed time is
 * routed into the cycle collector instead — see `flushSummary`. Threshold
 * is ignored in summary mode so even cheap steps show up in the table.
 */
export function perfMeasure(
  label: string,
  start: number,
  thresholdMs = 2,
): void {
  if (!isEnabled()) return;
  const ms = performance.now() - start;
  if (isSummary()) {
    recordStep(label, ms);
    return;
  }
  if (ms >= thresholdMs) {
    console.log(
      `%c[PERF] ${label}: ${ms.toFixed(1)}ms`,
      ms > 16 ? "color: red; font-weight: bold" : "color: orange",
    );
  }
}

/**
 * Log a single timestamp + label. Use for ordering events that happen
 * across components (e.g. "FlowsTable render start" vs each
 * "FlowRow render start") — the absolute `performance.now()` values let
 * you see staggering caused by concurrent-mode time-slicing.
 */
export function perfTimestamp(label: string): void {
  // Chatter — verbose only. Inside a single render burst these stamps
  // pile up by the dozen and bury the labelled benchmark lines.
  if (!isVerbose()) return;
  const now = performance.now();
  console.log(`%c[PERF] @${now.toFixed(1)} ${label}`, "color: #4a90e2");
}

// --- Render tracking ---

/**
 * Track render count for a component. Logs every Nth render (default 1).
 * Use at the top of a component body.
 */
export function useRenderCount(name: string, logEvery = 1): void {
  const countRef = useRef(0);
  countRef.current++;
  // Verbose-only — one line per component per render.
  if (isVerbose() && countRef.current % logEvery === 0) {
    console.log(`%c[PERF] ${name} render #${countRef.current}`, "color: #888");
  }
}

/**
 * Log which props/deps changed between renders.
 * Pass a record of named values to track.
 *
 * @example
 * useWhyDidYouRender('PartnerGroup', { preOrders, isOpen, partnerId });
 */
export function useWhyDidYouRender(
  name: string,
  deps: Record<string, unknown>,
): void {
  const prevRef = useRef<Record<string, unknown> | null>(null);

  // Verbose-only — produces one log per re-render per consumer.
  if (!isVerbose()) return;

  if (prevRef.current !== null) {
    const changes: string[] = [];
    for (const key of Object.keys(deps)) {
      if (prevRef.current[key] !== deps[key]) {
        changes.push(key);
      }
    }
    if (changes.length > 0) {
      console.log(
        `%c[PERF] ${name} re-rendered. Changed: ${changes.join(", ")}`,
        "color: #c59000",
      );
    }
  }
  prevRef.current = { ...deps };
}

/**
 * Log mount/unmount timing for a component.
 */
export function useMountTiming(name: string): void {
  const startRef = useRef(performance.now());

  useEffect(() => {
    // Verbose-only — mount/unmount fires for every PartnerGroup,
    // PreOrderSection, FlowsTable, FlowRow on every open.
    if (!isVerbose()) return;
    const mountTime = performance.now() - startRef.current;
    if (mountTime > 5) {
      console.log(
        `%c[PERF] ${name} mounted in ${mountTime.toFixed(1)}ms`,
        mountTime > 50 ? "color: red; font-weight: bold" : "color: orange",
      );
    }
    return () => {
      console.log(`%c[PERF] ${name} unmounted`, "color: #888");
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
}
